/* eslint-disable no-template-curly-in-string */

import { Banner, Button, Icon } from '@scale/scaleui';
import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import apiFetch from 'react-scale/lib/api-fetch';

export const ChatLazarusUIRenderer = ({
  src,
  allowRegeneration,
  lazarusModel,
  setContentUrl,
}: {
  src: string;
  allowRegeneration?: boolean;
  lazarusModel?: string;
  // Used to persist to response
  setContentUrl?: (url: string) => void;
}) => {
  const [signedContentUrl, setSignedContentUrl] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);
  const taskContext = useContext(ChatTaskContext);

  const {
    isFetching: isLoading,
    refetch: generateAndUpload,
    error,
  } = useQuery(
    ['ChatLazarusUIRenderer', src, lazarusModel, taskContext.assignmentId],
    async () => {
      const reactCodeRegex = new RegExp(
        '(?<=\\[#START JAVASCRIPT\\])[\\s\\S]*?(?=\\[#END JAVASCRIPT\\])',
      );

      // Need error handling for when the regex doesn't match
      const reactCode = src.match(reactCodeRegex)?.[0] ?? '';
      const bundleOutput = await apiFetch(null, 'internal/genai/invokeChatTool', {
        method: 'POST',
        body: {
          toolId: 'lazarus_react_to_html',
          params: {
            code: reactCode,
            model: lazarusModel,
          },
        },
      });

      // Need error handling for when the bundleOutput returns an stderr
      if (!bundleOutput.stdout || bundleOutput.stderr) {
        throw new Error(`${bundleOutput.stderr}`);
      }

      // Then upload the output to CDS
      const cdsOutput = await apiFetch(null, 'internal/contentUnderstanding/uploadRawContent', {
        method: 'POST',
        body: {
          assignmentId: taskContext.assignmentId,
          content: bundleOutput.stdout,
          mimeType: 'text/html',
        },
      });

      return cdsOutput;
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      onSuccess(cdsOutput) {
        setContentUrl?.(cdsOutput.contentUrl);
        setSignedContentUrl(cdsOutput.signedContentUrl);
        setErrorReason(null);
      },
      onError(err: Error) {
        setErrorReason(err.message);
      },
    },
  );

  return (
    <div className="flex flex-col my-2">
      <StepCard>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.HeaderColumn>
              <StepCard.Subtitle>
                <div className="flex justify-between">View Generated UI</div>
              </StepCard.Subtitle>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {(!signedContentUrl || allowRegeneration) && (
              <StepCard.Button
                icon="refresh"
                tooltip="Regenerate UI"
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  generateAndUpload();
                }}
                disabled={isLoading}
              />
            )}
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body>
          <div className="relative p-4">
            {isLoading && (
              <div className="text-center text-gray-500">Generating UI and uploading...</div>
            )}
            {signedContentUrl && !isLoading && !error && (
              <Button variant="primary" onClick={() => window.open(signedContentUrl, '_blank')}>
                <Icon size={'sm'} variant={'white'} icon={['fas', 'external-link']}></Icon>
                Open Generated UI In New Tab
              </Button>
            )}
            {error && (
              <Banner
                className="whitespace-pre-line break-words"
                variant="error"
                title={`Unable to generate and upload the bundled UI.\n\n${errorReason}`}
              />
            )}
            {!signedContentUrl && !isLoading && !error && (
              <div className="text-center text-gray-500">
                Click the generate button to create and upload the bundled UI
              </div>
            )}
          </div>
        </StepCard.Body>
      </StepCard>
    </div>
  );
};
